<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-28
-->

<script lang="ts" setup>
import { ref, watch, onUnmounted } from 'vue';
import { useI18n } from '#imports';
import type { Course } from '~/types/courses';

defineProps<{
  course: Course;
  isFlipped: boolean;
}>();

defineEmits<{
  (e: 'toggle-flip'): void;
}>();

const { t, locale } = useI18n();

const getMonthName = (m: number | null | undefined) => {
  if (!m) return '';
  const khMonths = [
    'មករា',
    'កុម្ភៈ',
    'មីនា',
    'មេសា',
    'ឧសភា',
    'មិថុនា',
    'កក្កដា',
    'សីហា',
    'កញ្ញា',
    'តុលា',
    'វិច្ឆិកា',
    'ធ្នូ',
  ];
  const enMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const index = m - 1;
  if (index < 0 || index > 11) return '';
  return locale.value === 'kh' ? khMonths[index] : enMonths[index];
};

const imageLoadErrors = ref<Record<string, boolean>>({});

const getUniqueSubjects = (course: Course) => {
  if (Array.isArray(course.courses)) {
    const subjects = course.courses.map((c) => c.subject?.name).filter(Boolean);
    return [...new Set(subjects)];
  }
  return course.subject?.name ? [course.subject.name] : [];
};

const secondsToTime = (s: number | null | undefined) => {
  if (s == null) return '';
  const h = Math.floor(s / 3600)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((s % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${h}:${m}`;
};

const formatDays = (days: string[] | null | undefined) => {
  if (!days || !days.length) return '';
  if (days.length === 7) {
    return t('courses.daily');
  }
  return days.map((d) => t(`courses.days.${d}`)).join(', ');
};

const showImageModal = ref(false);

const openImageModal = () => {
  showImageModal.value = true;
};

const closeImageModal = () => {
  showImageModal.value = false;
};

watch(showImageModal, (val) => {
  if (val) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
});

onUnmounted(() => {
  document.body.classList.remove('overflow-hidden');
});
</script>

<template>
  <div class="card-container group h-full w-full transition-transform duration-300 ease-out hover:-translate-y-2">
    <div class="card-inner relative h-full w-full" :class="{ 'is-flipped': isFlipped }">
      <!-- Front Side of Card -->
      <div
        class="card-front flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xs transition-all duration-300 group-hover:border-brand-blue/20 group-hover:shadow-lg"
        :class="{ 'cursor-pointer': course.type === 60 }"
        @click="course.type === 60 ? $emit('toggle-flip') : null"
      >
        <!-- Thumbnail Image / Gradient placeholder -->
        <div
          class="group/thumb relative aspect-video w-full shrink-0 overflow-hidden rounded-t-2xl border-b border-gray-100 bg-slate-100"
        >
          <AppImage
            v-if="course.thumbnail"
            :src="course.thumbnail"
            :alt="course.title"
            img-class="h-full w-full cursor-zoom-in object-cover transition-transform duration-300 group-hover/thumb:scale-105"
            loader-size="h-12 w-12"
            loader-overlay-class="bg-slate-50"
            loader-text=""
            @click.stop="openImageModal"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center bg-linear-to-tr from-brand-blue to-teal-600 text-white shadow-inner"
          >
            <Icon name="lucide:video" class="h-10 w-10 opacity-70" />
          </div>

          <!-- Eye icon button (appears on hover) -->
          <button
            v-if="course.thumbnail"
            type="button"
            class="absolute top-3 right-3 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/90 text-slate-700 opacity-0 shadow-md transition-all duration-300 group-hover/thumb:opacity-100 hover:scale-110 hover:bg-white hover:text-brand-blue focus:outline-none active:scale-95"
            @click.stop="openImageModal"
          >
            <Icon name="lucide:eye" class="h-4.5 w-4.5" />
          </button>

          <!-- Zoom Live pulsing indicator tag -->
          <div
            class="absolute top-3 left-3 flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-extrabold tracking-wide text-white uppercase backdrop-blur-xs"
          >
            <span class="relative flex h-1.5 w-1.5">
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"
              ></span>
              <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
            </span>
          </div>

          <!-- New Badge on top-right of thumbnail -->
          <div
            v-if="course.is_new === 1"
            class="absolute top-3 flex items-center rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-extrabold tracking-wide text-white uppercase shadow-sm transition-all duration-300"
            :class="course.thumbnail ? 'right-13' : 'right-3'"
          >
            {{ t('courses.new') }}
          </div>
        </div>

        <!-- Details -->
        <div class="flex flex-1 flex-col px-5 pt-6 pb-4">
          <!-- Teacher profile section / Package badge & lecturer btn -->
          <div class="relative z-10 -mt-12 mb-2 flex items-center justify-between">
            <div v-if="course.type !== 60" class="flex items-center gap-3">
              <div
                class="h-20 w-20 shrink-0 overflow-hidden rounded-2xl border-2 border-white bg-white shadow-md transition duration-300 group-hover:scale-105"
              >
                <AppImage
                  v-if="course.teacher?.photo && !imageLoadErrors[course.teacher._id]"
                  :src="course.teacher.photo"
                  :alt="course.teacher.name"
                  img-class="h-full w-full object-cover"
                  loader-size="h-8 w-8"
                  loader-overlay-class="bg-slate-50"
                  loader-text=""
                  @error="imageLoadErrors[course.teacher._id] = true"
                />
                <div
                  v-else
                  class="flex h-full w-full items-center justify-center bg-brand-blue/5 font-sans text-xs font-bold text-brand-blue uppercase"
                >
                  {{ course.teacher?.name ? course.teacher.name.substring(0, 2) : '👨‍🏫' }}
                </div>
              </div>
              <div class="min-w-0 py-1">
                <span class="text-[12px] font-bold tracking-wider text-slate-400 uppercase">
                  {{ t('courses.instructor') }}
                </span>
                <span class="text-md ml-1 font-sans font-black text-slate-700">
                  {{ course.teacher?.name || t('courses.na') }}
                </span>
              </div>
            </div>

            <!-- Package Info & Lecturer Flip button -->
            <div v-else class="flex w-full items-center justify-between">
              <!-- Package Badge -->
              <div
                class="flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700"
              >
                <Icon name="lucide:layers" class="h-4 w-4 shrink-0" />
                <span>{{ t('courses.packageCourse') }}</span>
              </div>

              <!-- Flip Button (with Lecturer/Teacher icon) -->
              <button
                type="button"
                class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-100 bg-white text-slate-700 shadow-md transition duration-300 hover:scale-110 hover:border-brand-blue hover:text-brand-blue focus:outline-none active:scale-95"
                :title="t('courses.viewInstructors')"
                @click.stop="$emit('toggle-flip')"
              >
                <Icon name="lucide:presentation" class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- Subject & Grade Badges -->
          <div class="mb-1.5 flex flex-wrap items-center gap-1.5">
            <span
              v-for="subName in getUniqueSubjects(course)"
              :key="subName"
              class="inline-block rounded-md bg-brand-blue/5 px-2 py-0.5 text-[10px] font-bold text-brand-blue uppercase"
            >
              {{ subName }}
            </span>
            <span
              v-if="course.grade?.name"
              class="inline-block rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 uppercase"
            >
              {{ course.grade.name }}
            </span>
          </div>

          <!-- Title -->
          <UTooltip class="w-full" :arrow="true" :ui="{ content: 'h-auto' }">
            <template #content>
              <div class="max-w-[200px] p-1.5 leading-relaxed break-words whitespace-normal">
                {{ course.title }}
              </div>
            </template>
            <h3
              class="mt-1.5 line-clamp-1 text-sm leading-snug font-extrabold text-slate-900 group-hover:text-brand-blue"
            >
              {{ course.title }}
            </h3>
          </UTooltip>

          <!-- Zoom Time Schedule -->
          <div
            v-if="course.time && (course.time.days?.length || course.time.start != null)"
            class="mt-2 mb-3 flex items-center gap-2 rounded-xl bg-slate-50 p-2 text-xs text-slate-600"
          >
            <Icon name="lucide:calendar-days" class="h-4 w-4 shrink-0 text-brand-blue" />
            <div class="flex min-w-0 flex-col">
              <span v-if="course.time.days?.length" class="truncate font-bold">
                {{ formatDays(course.time.days) }}
              </span>
              <span v-if="course.time.start != null && course.time.end != null" class="text-[11px] text-slate-500">
                {{ secondsToTime(course.time.start) }} - {{ secondsToTime(course.time.end) }}
              </span>
            </div>
          </div>

          <!-- Package Inclusions (Overlapping Avatars & Counts for type === 60) -->
          <CoursesCardInclusions v-else-if="course.type === 60" :course="course" class="mt-5" />

          <!-- Price section -->
          <div class="-mx-5 mt-auto flex shrink-0 items-baseline justify-between border-t border-slate-100 px-5 pt-3">
            <div class="flex flex-col">
              <span class="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                {{ t('courses.tuitionFee') }}
              </span>
              <div class="mt-1 flex items-baseline gap-2">
                <span class="text-lg font-black text-brand-blue">
                  ${{ course.price?.year || 0
                  }}<span class="text-xs font-normal text-slate-500">/{{ t('courses.yr') }}</span>
                </span>
                <span
                  v-if="course.price?.discount && course.price.discount > (course.price.year || 0)"
                  class="text-xs font-medium text-slate-400 line-through"
                >
                  ${{ course.price.discount }}
                </span>
              </div>
            </div>

            <!-- Start Month Info (aligns right) -->
            <div
              v-if="course.price?.month && getMonthName(course.price.month)"
              class="flex flex-col items-end self-end pb-[2px] text-right"
            >
              <span class="rounded-md bg-brand-blue/5 px-2.5 py-1 text-[10px] font-bold text-brand-blue">
                {{ t('courses.startAt', { month: getMonthName(course.price.month) }) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Back Side of Card -->
      <CoursesCardBack v-if="course.type === 60" :course="course" @toggle-flip="$emit('toggle-flip')" />
    </div>
  </div>

  <!-- Image Popup Modal -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showImageModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-md"
        @click="closeImageModal"
      >
        <!-- Close Button -->
        <button
          type="button"
          class="absolute top-4 right-4 z-55 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-all duration-200 hover:scale-105 hover:bg-white/20 focus:outline-none active:scale-95"
          @click="closeImageModal"
        >
          <Icon name="lucide:x" class="h-6 w-6" />
        </button>

        <!-- Image Container -->
        <div
          class="relative flex h-full w-full max-w-[1200px] items-center justify-center p-2 transition-transform duration-200 md:p-6"
          @click.stop
        >
          <img
            v-if="course.thumbnail"
            :src="course.thumbnail"
            :alt="course.title"
            class="max-h-full max-w-full rounded-2xl object-contain shadow-2xl select-none"
            @contextmenu.prevent
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.font-sans {
  font-family:
    'Kantumruy Pro',
    'Kantumruy',
    'Ubuntu',
    system-ui,
    -apple-system,
    sans-serif;
}

/* 3D Flip Card Styles */
.card-container {
  perspective: 1000px;
}

.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
}

.card-inner.is-flipped {
  transform: rotateY(180deg);
}

.card-front {
  position: relative;
  z-index: 2;
}

.card-back {
  position: absolute;
  inset: 0;
  z-index: 1;
  transform: rotateY(180deg);
}

.card-front,
.card-back {
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.card-inner.is-flipped .card-front {
  z-index: 1;
  pointer-events: none;
}

.card-inner.is-flipped .card-back {
  z-index: 2;
  pointer-events: auto;
}
</style>
